// App shell — header on top, content in the middle, bottom nav on mobile.
// On desktop (md+), the bottom nav transforms into a left sidebar with the
// Notifications entry and user menu pinned to the bottom. On mobile the
// NotificationBell stays in the top Header (see Header.tsx).

import { Suspense, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { Header } from './Header';
import { BottomNav } from './BottomNav';
import { UserMenu } from './UserMenu';
import { Toaster } from './Toaster';
import { PushNoticeListener } from './PushNoticeListener';
import { ConfirmRoot } from './ConfirmRoot';
import { StepUpRoot } from './StepUpRoot';
import { UpdateCard } from './UpdateCard';
import { logPageView, logSessionStart } from '../lib/usage';
import { syncDisplayFormat } from '../lib/displayFormatSync';
import { useAuth } from '../lib/auth';

export function AppShell() {
  const location = useLocation();
  const { user, isEmulated, toggleEmulation } = useAuth();
  // Keyed on the uid, not the user object: the shell can mount from the
  // persisted session and then receive Firebase Auth's confirmation of the same
  // account, which must not count as a second session or page view. Both
  // helpers wait for Auth themselves (lib/usage.ts).
  const uid = user?.uid;

  // Log session start on mount once authenticated, and record how this
  // person reads dates (only when it changed) so server-written messages use
  // their notation and time zone — see lib/displayFormatSync.ts.
  useEffect(() => {
    if (uid) {
      void logSessionStart();
      void syncDisplayFormat();
    }
  }, [uid]);

  // Log page views on route changes
  useEffect(() => {
    if (uid) {
      void logPageView(location.pathname);
    }
  }, [location.pathname, uid]);

  // min-h-dvh, not the 100vh screen height: iOS Safari's 100vh is the
  // viewport with its toolbars hidden, taller than what is visible, which let
  // the whole shell scroll and carry the header up under the status bar.
  return (
    <div className="flex h-full min-h-dvh flex-col bg-n-50">
      {/* In-app toasts. Fixed-position overlay, rendered once here so any
          route can showToast() without its own UI. */}
      <Toaster />

      {/* A push that arrives while the app is in front shows as a toast
          instead of a system notification, where the browser allows it
          (lib/pushNotice.ts). Listening only here, inside the signed-in
          shell, is what keeps a notice from being claimed where no toast
          can be seen. */}
      <PushNoticeListener />

      {/* Shared confirmation dialog (see lib/confirm.ts). Rendered once here so
          any route can await confirmDialog() instead of native window.confirm. */}
      <ConfirmRoot />

      {/* WebAuthn step-up modal (see lib/stepUp.ts, IMP-045). Rendered once so a
          gated callable returning `step-up-required` can prompt + retry. */}
      <StepUpRoot />

      {/* Emulation Banner at the absolute top of the viewport. On mobile it is
          the topmost element, so it (not the header) clears the status bar. */}
      {isEmulated && (
        <div className="bg-ol-orange text-white px-4 pb-2.5 pt-safe-3 md:pt-2.5 text-center text-xs font-bold flex items-center justify-center gap-2 z-50 shadow-md shrink-0">
          <span>Viewing app as standard employee (Emulation Mode)</span>
          <button
            onClick={toggleEmulation}
            className="underline hover:text-ol-orange-50 font-bold ml-1.5 cursor-pointer focus:outline-none"
          >
            Switch Back to Admin
          </button>
        </div>
      )}

      <div className="flex flex-1 flex-col md:flex-row min-h-0">
        {/* Sidebar on desktop. Note: the inner nav is `flex flex-col` so the
            Admin Console pin (rendered with mt-auto inside BottomNav) can
            push itself to the bottom of the visible nav, above the UserMenu. */}
        <aside className="hidden md:flex md:w-60 md:flex-col md:border-r md:border-cloud md:bg-white">
          <Header variant="sidebar" />
          <nav className="flex flex-1 flex-col overflow-y-auto px-3 py-4">
            <BottomNav variant="sidebar" />
          </nav>
          {/* User menu pinned to the bottom of the sidebar */}
          <div className="border-t border-cloud px-2 py-2">
            <UserMenu variant="row" />
          </div>
        </aside>

        {/* Mobile header (hidden on desktop) */}
        <Header variant="mobile" safeTop={!isEmulated} className="md:hidden animate-fade-in" />

        {/* Main content. The desktop top bar that previously carried the
            NotificationBell was removed (IMP-021) — the bell now lives in the
            sidebar as a bottom-pinned entry — reclaiming that vertical space
            for the content area. The mobile Header above still carries the
            bell on small screens. */}
        <main className="flex-1 overflow-y-auto px-4 pb-24 pt-4 md:px-8 md:pb-8 md:pt-6">
          {/* Keep the shell chrome mounted while a lazy route chunk loads
              (IMP-046); only the content area shows the fallback. Uses the
              shared .loading-state recipe + Loader2 icon (the sanctioned
              loading affordance) rather than a hand-rolled spinner. */}
          <Suspense
            fallback={
              <div className="loading-state">
                <Loader2 className="animate-spin text-orange" size={24} />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </main>

        {/* Bottom nav on mobile */}
        <BottomNav
          variant="bottom"
          className="fixed inset-x-0 bottom-0 z-30 border-t border-cloud bg-white pb-safe md:hidden"
        />
      </div>

      {/* "Update available" card (IMP-064; see lib/appUpdate.ts). Last in the
          DOM so keyboard order matches where it sits: after the page, just
          above the bottom nav. */}
      <UpdateCard />
    </div>
  );
}
